import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Building2, TrendingUp, Users, Calendar } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  closed: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function AdminFundProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFunds = useCallback(async () => {
    try {
      const fundIds = user?.fund_ids || [];
      if (fundIds.length === 0) { setLoading(false); return; }
      const results = await Promise.all(fundIds.map(id => API.get(`/funds/${id}`).catch(() => null)));
      setFunds(results.filter(Boolean).map(r => r.data));
    } catch { toast.error("Failed to load funds"); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadFunds(); }, [loadFunds]);

  // If only 1 fund, go directly to its detail page
  useEffect(() => {
    if (!loading && funds.length === 1) {
      navigate(`/admin/fund/${funds[0].id}`, { replace: true });
    }
  }, [loading, funds, navigate]);

  if (loading || funds.length === 1) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-fund-list">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Fund Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your assigned funds</p>
      </div>

      {funds.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm">
            <Building2 className="w-10 h-10 mb-3 opacity-30" />
            No funds assigned to your account.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {funds.map(fund => (
            <Card
              key={fund.id}
              className="cursor-pointer group hover:border-primary/30 transition-all duration-200 border-border/50"
              onClick={() => navigate(`/admin/fund/${fund.id}`)}
              data-testid={`fund-card-${fund.id}`}
            >
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {fund.logo_file_name ? (
                      <div className="w-10 h-10 rounded-lg border border-border bg-secondary/30 flex items-center justify-center overflow-hidden">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/funds/${fund.id}/logo`} alt="" className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-heading text-base font-semibold group-hover:text-primary transition-colors">{fund.name}</h3>
                      <p className="text-xs text-muted-foreground">{fund.fund_type || "No type set"}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[9px] uppercase tracking-wider ${STATUS_STYLES[fund.status] || ""}`}>{fund.status}</Badge>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-1">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1 text-muted-foreground"><TrendingUp className="w-3 h-3" /><span className="text-[10px] uppercase tracking-wider">NAV</span></div>
                    <p className="text-sm font-semibold tabular-nums">${(fund.nav || 0).toLocaleString()}</p>
                  </div>
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1 text-muted-foreground"><Users className="w-3 h-3" /><span className="text-[10px] uppercase tracking-wider">Offerings</span></div>
                    <p className="text-sm font-semibold">{fund.offerings?.length || 0}</p>
                  </div>
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1 text-muted-foreground"><Calendar className="w-3 h-3" /><span className="text-[10px] uppercase tracking-wider">Frequency</span></div>
                    <p className="text-sm font-semibold capitalize">{fund.reporting_frequency || "-"}</p>
                  </div>
                </div>

                {fund.offerings?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {fund.offerings.map((o, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px] px-2 py-0">{o.name || `Offering ${i + 1}`}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
